import React, { useEffect, useState, useRef } from 'react'
import { Box, Grid, Typography, Card, CardContent } from '@material-ui/core'
import DateRangeSelector from '../../modalities-machine-logs/components/DateRangeSelector'
import GaugeChart from './GaugeChart'
import LineChart from './LineChart'
import { getModalityConfig, getModalityMetrics, supportsChartType } from '../config/modalityConfigs'
import * as api from '../../data'

const MetricsDashboard = ({ modalityId, modality: modalityProp }) => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [modality, setModality] = useState(modalityProp)
  const [dateRange, setDateRange] = useState({ startDate: '-7d', endDate: null })
  const lastFetchedModalityIdRef = useRef(null)
  const lastFetchedMetricsKeyRef = useRef('')

  // Fetch modality information to determine type (only if not provided as prop)
  const fetchModality = async () => {
    // Prevent duplicate fetches
    if (lastFetchedModalityIdRef.current === modalityId) return
    
    lastFetchedModalityIdRef.current = modalityId
    try {
      const modalityData = await api.modalities.fetch(modalityId)
      setModality(modalityData)
      
      // Set default time range based on modality type
      const config = getModalityConfig(modalityData.modality)
      setDateRange({ startDate: config.defaultTimeRange, endDate: null })
    } catch (error) {
      console.error('Error fetching modality:', error)
      lastFetchedModalityIdRef.current = null // Reset on error to allow retry
    }
  }

  // Update local state when prop changes
  useEffect(() => {
    if (modalityProp) {
      setModality(modalityProp)
      // Set default time range based on modality type
      const config = getModalityConfig(modalityProp.modality)
      const defaultDateRange = { startDate: config.defaultTimeRange, endDate: null }
      // Only update dateRange if it's different to avoid triggering unnecessary fetches
      setDateRange(prev => {
        if (prev.startDate !== defaultDateRange.startDate || prev.endDate !== defaultDateRange.endDate) {
          return defaultDateRange
        }
        return prev
      })
    }
  }, [modalityProp])

  const fetchDashboard = async () => {
    if (!modality) return
    
    // Create a unique key for this fetch to prevent duplicates
    const fetchKey = `${modalityId}-${dateRange.startDate}-${dateRange.endDate || 'null'}`
    if (lastFetchedMetricsKeyRef.current === fetchKey) return
    
    lastFetchedMetricsKeyRef.current = fetchKey
    try {
      setLoading(true)
      const res = await api.modalities.fetchDashboardMetrics(modalityId, dateRange)
      
      // Filter out helium level values under 5 for plotting
      // This prevents displaying invalid/zero helium readings that can skew charts
      if (res?.graphs?.heliumLevel) {
        const originalCount = res.graphs.heliumLevel.values?.length || 0
        
        // Filter the values array for line charts
        if (res.graphs.heliumLevel.values) {
          res.graphs.heliumLevel.values = res.graphs.heliumLevel.values.filter(item => {
            const value = parseFloat(item.value)
            return value >= 5  // Only include values 5 and above
          })
        }
        
        const filteredCount = res.graphs.heliumLevel.values?.length || 0
        if (originalCount > 0 && filteredCount < originalCount) {
          console.log(`Filtered ${originalCount - filteredCount} helium level values under 5% (${filteredCount} valid values remaining)`)
        }
        
        // Update the current value for gauge chart to use the most recent valid value
        if (res.graphs.heliumLevel.values && res.graphs.heliumLevel.values.length > 0) {
          const validValues = res.graphs.heliumLevel.values.filter(item => {
            const value = parseFloat(item.value)
            return value >= 5
          })
          
          if (validValues.length > 0) {
            // Use the most recent valid value for the gauge
            const mostRecentValid = validValues[validValues.length - 1]
            res.graphs.heliumLevel.value = mostRecentValid.value
          }
        }
      }
      
      setDashboardData(res)
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error)
      lastFetchedMetricsKeyRef.current = '' // Reset on error to allow retry
    } finally {
      setLoading(false)
    }
  }

  // Only fetch modality if not provided as prop
  useEffect(() => {
    if (!modalityProp) {
      fetchModality()
    }
    // Reset ref when modalityId changes
    return () => {
      if (lastFetchedModalityIdRef.current !== modalityId) {
        lastFetchedModalityIdRef.current = null
      }
    }
  }, [modalityId, modalityProp])

  useEffect(() => {
    if (modality) {
      fetchDashboard()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modality, dateRange])

  const handleTimeframeChange = (start, end) => {
    setDateRange({ startDate: start, endDate: end })
  }

  // Get metrics configuration based on modality type and manufacturer
  const getMetricsConfig = () => {
    if (!modality) return []
    return getModalityMetrics(modality.modality, modality.manufacturer)
  }

  const metricsConfig = getMetricsConfig()

  if (!modality) {
    return (
      <Box p={2}>
        <Typography>Loading modality information...</Typography>
      </Box>
    )
  }

  return (
    <Box p={1}>
      <Box mb={1} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h6" gutterBottom>
            {modality.modality} Metrics Dashboard
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            {modality.displayName} - {modality.manufacturer}
          </Typography>
        </Box>
        
        <Box>
          <DateRangeSelector onChange={handleTimeframeChange} />
        </Box>
      </Box>
      
      {/* Combined Gauge and Line Charts - Side by Side */}
      <Grid container spacing={2}>
        {metricsConfig.map((metric) => {
          const metricData = dashboardData?.graphs?.[metric.key]
          
          return (
            <Grid item xs={12} key={metric.key}>
              <Card>
                <CardContent style={{ padding: '12px' }}>
                  <Typography variant="h6" gutterBottom align="center" style={{ marginBottom: '8px' }}>
                    {metric.label}
                  </Typography>
                  
                  <Grid container spacing={1}>
                    {/* Gauge Chart */}
                    {supportsChartType(modality.modality, 'gauge', modality.manufacturer) && (
                      <Grid item xs={12} md={supportsChartType(modality.modality, 'line', modality.manufacturer) ? 6 : 12}>
                        <Box display="flex" justifyContent="center">
                          <GaugeChart 
                            label={metric.label} 
                            data={metricData} 
                            loading={loading}
                            unit={metric.unit}
                            min={metric.min}
                            max={metric.max}
                          />
                        </Box>
                      </Grid>
                    )}
                    
                    {/* Line Chart */}
                    {supportsChartType(modality.modality, 'line', modality.manufacturer) && (
                      <Grid item xs={12} md={supportsChartType(modality.modality, 'gauge', modality.manufacturer) ? 6 : 12}>
                        <Box display="flex" justifyContent="center">
                          <LineChart
                            label={metric.label}
                            data={metricData?.values}
                            unit={metric.unit}
                            loading={loading}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

export default MetricsDashboard
